/**
 * Browser configuration. Only publishable values may reach the bundle.
 * A privileged key in the browser would bypass RLS, so it is refused outright.
 */
export interface CommandConfig {
  readonly supabaseUrl: string;
  readonly publishableKey: string;
  readonly authRedirectUrl: string;
}

export type ConfigResult =
  | { readonly ok: true; readonly config: CommandConfig }
  | { readonly ok: false; readonly problems: readonly string[] };

function decodeJwtRole(key: string): string | null {
  const parts = key.split('.');
  if (parts.length !== 3 || !parts[1]) return null;
  try {
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as { role?: unknown };
    return typeof payload.role === 'string' ? payload.role : null;
  } catch {
    return null;
  }
}

/** True when a key would grant more than browser (anon/publishable) authority. */
export function isPrivilegedKey(key: string): boolean {
  if (key.startsWith('sb_secret_')) return true;
  const role = decodeJwtRole(key);
  return role !== null && role !== 'anon';
}

export function readConfig(env: Readonly<Record<string, string | undefined>>): ConfigResult {
  const problems: string[] = [];
  const supabaseUrl = env.VITE_BRUNEL_SUPABASE_URL?.trim() ?? '';
  const publishableKey = env.VITE_BRUNEL_SUPABASE_PUBLISHABLE_KEY?.trim() ?? '';
  const authRedirectUrl = env.VITE_BRUNEL_AUTH_REDIRECT_URL?.trim() ?? '';

  let parsedUrl: URL | null = null;
  try {
    parsedUrl = new URL(supabaseUrl);
  } catch {
    problems.push('VITE_BRUNEL_SUPABASE_URL is missing or not a URL');
  }
  if (parsedUrl && parsedUrl.protocol !== 'https:' && parsedUrl.hostname !== '127.0.0.1' && parsedUrl.hostname !== 'localhost') {
    problems.push('VITE_BRUNEL_SUPABASE_URL must use https outside local development');
  }
  if (!publishableKey) problems.push('VITE_BRUNEL_SUPABASE_PUBLISHABLE_KEY is missing');
  else if (isPrivilegedKey(publishableKey)) problems.push('A privileged key was supplied; only publishable keys are allowed in the browser');
  // OAuth returns to window.location.origin at runtime. A baked redirect is optional:
  // requiring one forces every Cloudflare preview to hardcode a hostname.
  if (authRedirectUrl !== '') {
    try {
      new URL(authRedirectUrl);
    } catch {
      problems.push('VITE_BRUNEL_AUTH_REDIRECT_URL is not a URL');
    }
  }

  if (problems.length > 0) return { ok: false, problems };
  return { ok: true, config: { supabaseUrl, publishableKey, authRedirectUrl } };
}

/** Every static Command bundle is deployable, so every build must be configured. */
export function commandBuildPublicEnvProblems(env: Readonly<Record<string, string | undefined>>): readonly string[] {
  const result = readConfig(env);
  return result.ok ? [] : result.problems;
}

/**
 * Local builds may load publishable values from .env.local. Cloudflare and the
 * direct production helper must receive them from their own process environment
 * so a developer's local project can never become production configuration.
 */
export function commandBuildEnvironment(
  fileEnv: Readonly<Record<string, string | undefined>>,
  processEnv: Readonly<Record<string, string | undefined>>,
): Readonly<Record<string, string | undefined>> {
  if (processEnv.CF_PAGES === '1' || processEnv.BRUNEL_REQUIRE_PUBLIC_ENV === '1') return processEnv;
  return { ...fileEnv, ...processEnv };
}

/**
 * Where the planner function listens.
 *
 * Derived from the database URL the browser is already given, so the project
 * reference is never written into source and no new value has to be supplied
 * to the deployment.
 */
export function plannerUrl(supabaseUrl: string): string {
  return new URL('/functions/v1/brunel-planner', supabaseUrl).toString();
}
